import { useCallback, useEffect, useRef, useState } from 'react';
import { Download, Loader2 } from 'lucide-react';

const W = 1200;
const H = 630;

const loadImage = (src) =>
  new Promise((resolve) => {
    if (!src) return resolve(null);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });

const roundRect = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
};

const initials = (name = '') =>
  name.trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase() || '').join('');

export const SaleGraphic = ({ sale, agentName, agentPicture }) => {
  const canvasRef = useRef(null);
  const [ready, setReady] = useState(false);

  const draw = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas || !sale) return;
    const ctx = canvas.getContext('2d');
    const isAnnuity = sale.category === 'annuity';
    const accent = isAnnuity ? '#01F5C4' : '#23ACD5';

    try { await document.fonts.ready; } catch { /* noop */ }
    const [logo, avatar] = await Promise.all([loadImage('/breeze-logo-trim.png'), loadImage(agentPicture)]);

    // Background
    const bg = ctx.createLinearGradient(0, 0, W, H);
    bg.addColorStop(0, '#0B1437');
    bg.addColorStop(0.55, '#111C44');
    bg.addColorStop(1, '#0A1030');
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, W, H);

    // Glows
    const glow = ctx.createRadialGradient(1010, 120, 0, 1010, 120, 520);
    glow.addColorStop(0, `${accent}4D`);
    glow.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, W, H);
    const glow2 = ctx.createRadialGradient(90, 600, 0, 90, 600, 420);
    glow2.addColorStop(0, 'rgba(67,24,255,0.40)');
    glow2.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = glow2;
    ctx.fillRect(0, 0, W, H);

    // Grid
    ctx.strokeStyle = 'rgba(255,255,255,0.045)';
    ctx.lineWidth = 2;
    for (let i = 1; i < 12; i += 1) {
      ctx.beginPath(); ctx.moveTo((W / 12) * i, 0); ctx.lineTo((W / 12) * i, H); ctx.stroke();
    }
    for (let i = 1; i < 7; i += 1) {
      ctx.beginPath(); ctx.moveTo(0, (H / 7) * i); ctx.lineTo(W, (H / 7) * i); ctx.stroke();
    }

    // Accent border
    ctx.strokeStyle = `${accent}66`;
    ctx.lineWidth = 5;
    roundRect(ctx, 22, 22, W - 44, H - 44, 34);
    ctx.stroke();

    // Logo
    if (logo) {
      const lw = 240;
      ctx.drawImage(logo, 58, 52, lw, (logo.height / logo.width) * lw);
    } else {
      ctx.fillStyle = '#FFFFFF';
      ctx.font = "800 40px Manrope, sans-serif";
      ctx.fillText('BREEZE', 58, 90);
    }

    // Status pill (top right)
    const pillText = sale.status === 'approved' ? 'APPROVED' : 'IN UNDERWRITING';
    const pillColor = sale.status === 'approved' ? '#01B574' : '#FF9000';
    ctx.font = "800 22px Manrope, sans-serif";
    const pw = ctx.measureText(pillText).width + 48;
    ctx.fillStyle = `${pillColor}2A`;
    roundRect(ctx, W - 58 - pw, 52, pw, 50, 25);
    ctx.fill();
    ctx.strokeStyle = pillColor;
    ctx.lineWidth = 3;
    roundRect(ctx, W - 58 - pw, 52, pw, 50, 25);
    ctx.stroke();
    ctx.fillStyle = pillColor;
    ctx.textAlign = 'center';
    ctx.fillText(pillText, W - 58 - pw / 2, 84);
    ctx.textAlign = 'left';

    // Eyebrow
    ctx.fillStyle = accent;
    ctx.font = "800 22px Manrope, sans-serif";
    ctx.letterSpacing = '5px';
    ctx.fillText(isAnnuity ? 'NEW ANNUITY' : 'NEW SALE', 58, 196);
    ctx.letterSpacing = '0px';

    // Headline
    ctx.fillStyle = '#FFFFFF';
    ctx.font = "800 62px Manrope, sans-serif";
    ctx.fillText('Another family', 58, 274);
    ctx.fillText('protected.', 58, 344);

    // Carrier / product chips
    ctx.font = "700 26px Manrope, sans-serif";
    let cx = 58;
    [sale.carrier, sale.product_type].forEach((label) => {
      const cw = ctx.measureText(label).width + 48;
      ctx.fillStyle = 'rgba(255,255,255,0.08)';
      roundRect(ctx, cx, 388, cw, 58, 29);
      ctx.fill();
      ctx.fillStyle = 'rgba(255,255,255,0.85)';
      ctx.fillText(label, cx + 24, 425);
      cx += cw + 16;
    });

    // Amount panel (right)
    const px = 690;
    const pwid = W - px - 58;
    ctx.fillStyle = 'rgba(255,255,255,0.05)';
    roundRect(ctx, px, 166, pwid, 210, 30);
    ctx.fill();
    ctx.strokeStyle = 'rgba(255,255,255,0.10)';
    ctx.lineWidth = 3;
    roundRect(ctx, px, 166, pwid, 210, 30);
    ctx.stroke();

    ctx.fillStyle = 'rgba(255,255,255,0.5)';
    ctx.font = "700 22px Manrope, sans-serif";
    ctx.letterSpacing = '3px';
    ctx.textAlign = 'center';
    ctx.fillText(isAnnuity ? 'ROLLOVER AMOUNT' : 'ANNUAL PREMIUM', px + pwid / 2, 226);
    ctx.letterSpacing = '0px';

    const amount = `$${Math.round(sale.annual_premium).toLocaleString()}`;
    let size = 88;
    ctx.font = `800 ${size}px Manrope, sans-serif`;
    while (ctx.measureText(amount).width > pwid - 64 && size > 44) {
      size -= 4;
      ctx.font = `800 ${size}px Manrope, sans-serif`;
    }
    ctx.save();
    ctx.shadowColor = accent;
    ctx.shadowBlur = 38;
    ctx.fillStyle = accent;
    ctx.fillText(amount, px + pwid / 2, 322);
    ctx.restore();
    ctx.textAlign = 'left';

    // Divider
    ctx.strokeStyle = 'rgba(255,255,255,0.08)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(58, 492);
    ctx.lineTo(W - 58, 492);
    ctx.stroke();

    // Agent row
    const ay = 552;
    ctx.save();
    ctx.beginPath();
    ctx.arc(102, ay, 42, 0, Math.PI * 2);
    ctx.closePath();
    ctx.clip();
    if (avatar) {
      const side = Math.min(avatar.width, avatar.height);
      ctx.drawImage(avatar, (avatar.width - side) / 2, (avatar.height - side) / 2, side, side, 60, ay - 42, 84, 84);
    } else {
      ctx.fillStyle = accent;
      ctx.fillRect(60, ay - 42, 84, 84);
      ctx.fillStyle = '#0B1437';
      ctx.font = "800 34px Manrope, sans-serif";
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(initials(agentName), 102, ay);
      ctx.textAlign = 'left';
      ctx.textBaseline = 'alphabetic';
    }
    ctx.restore();
    ctx.strokeStyle = accent;
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.arc(102, ay, 44, 0, Math.PI * 2);
    ctx.stroke();

    ctx.fillStyle = '#FFFFFF';
    ctx.font = "800 34px Manrope, sans-serif";
    ctx.fillText(agentName || 'Breeze Agent', 168, ay - 4);
    ctx.fillStyle = 'rgba(255,255,255,0.45)';
    ctx.font = "600 24px Manrope, sans-serif";
    ctx.fillText('Breeze Financial Group', 168, ay + 30);

    ctx.textAlign = 'right';
    ctx.fillStyle = 'rgba(255,255,255,0.45)';
    ctx.font = "700 24px Manrope, sans-serif";
    ctx.fillText(
      new Date(`${sale.sale_date}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      W - 58,
      ay + 10
    );
    ctx.textAlign = 'left';

    setReady(true);
  }, [sale, agentName, agentPicture]);

  useEffect(() => { draw(); }, [draw]);

  const download = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `breeze-sale-${sale.sale_date}.png`;
      a.click();
      URL.revokeObjectURL(url);
    }, 'image/png');
  };

  return (
    <div className="space-y-4" data-testid="sale-graphic">
      <div className="relative overflow-hidden rounded-2xl border border-[#E9EDF7] bg-[#0B1437]">
        <canvas ref={canvasRef} width={W} height={H} className="block w-full" data-testid="sale-graphic-canvas" />
        {!ready && (
          <div className="absolute inset-0 flex items-center justify-center bg-[#0B1437]">
            <Loader2 className="h-8 w-8 animate-spin text-[#23ACD5]" />
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={download}
        disabled={!ready}
        className="flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#23ACD5] to-[#00E5FF] px-6 py-3.5 font-['Manrope'] text-sm font-extrabold text-[#06263A] shadow-[0_8px_28px_rgba(35,172,213,0.45)] transition-transform hover:scale-[1.01] disabled:opacity-60"
        data-testid="sale-graphic-download-btn"
      >
        <Download className="h-4 w-4" />
        Save graphic for social
      </button>
    </div>
  );
};
